import type { RematchMessage } from "./protocol";

export interface RematchTransition {
  readonly accepted: boolean;
  readonly declined: boolean;
  readonly ignored: boolean;
  readonly sendAccept: boolean;
  readonly ready: boolean;
}

export interface RematchAgreementSnapshot {
  readonly round: number;
  readonly proposedMatchId: string;
  readonly localRequested: boolean;
  readonly remoteRequested: boolean;
  readonly localAccepted: boolean;
  readonly remoteAccepted: boolean;
  readonly declined: boolean;
  readonly restarting: boolean;
  readonly ready: boolean;
}

const NO_CHANGE: RematchTransition = {
  accepted: false,
  declined: false,
  ignored: true,
  sendAccept: false,
  ready: false,
};

/**
 * Per-round rematch agreement. Both peers must request and both peers must see
 * the other's accept before either side is allowed to tear down the channel.
 */
export class RematchAgreement {
  readonly round: number;
  readonly proposedMatchId: string;
  private localRequestedValue = false;
  private remoteRequestedValue = false;
  private localAcceptedValue = false;
  private remoteAcceptedValue = false;
  private declinedValue = false;
  private restartingValue = false;

  constructor(round: number, proposedMatchId: string) {
    if (!Number.isSafeInteger(round) || round < 1 || !proposedMatchId) {
      throw new RangeError("A positive rematch round and proposed match id are required");
    }
    this.round = round;
    this.proposedMatchId = proposedMatchId;
  }

  get localRequested(): boolean {
    return this.localRequestedValue;
  }

  get remoteRequested(): boolean {
    return this.remoteRequestedValue;
  }

  get ready(): boolean {
    return (
      !this.declinedValue &&
      this.localRequestedValue &&
      this.remoteRequestedValue &&
      this.localAcceptedValue &&
      this.remoteAcceptedValue
    );
  }

  requestLocal(): RematchTransition {
    if (this.declinedValue || this.restartingValue || this.localRequestedValue) {
      return { ...NO_CHANGE, ready: this.ready };
    }
    this.localRequestedValue = true;
    const sendAccept = this.remoteRequestedValue && !this.localAcceptedValue;
    if (sendAccept) this.localAcceptedValue = true;
    return {
      accepted: false,
      declined: false,
      ignored: false,
      sendAccept,
      ready: this.ready,
    };
  }

  receive(message: RematchMessage): RematchTransition {
    if (
      this.declinedValue ||
      this.restartingValue ||
      message.round !== this.round ||
      message.proposedMatchId !== this.proposedMatchId
    ) {
      return { ...NO_CHANGE, ready: this.ready };
    }

    if (message.action === "decline") {
      this.declinedValue = true;
      return {
        accepted: false,
        declined: true,
        ignored: false,
        sendAccept: false,
        ready: false,
      };
    }

    if (message.action === "request") {
      this.remoteRequestedValue = true;
      const sendAccept = this.localRequestedValue && !this.localAcceptedValue;
      if (sendAccept) this.localAcceptedValue = true;
      return {
        accepted: false,
        declined: false,
        ignored: false,
        sendAccept,
        ready: this.ready,
      };
    }

    // An accept is meaningful only after the same peer's request. Control is
    // ordered, so a valid request always precedes its accept.
    if (!this.remoteRequestedValue || !this.localRequestedValue) {
      return { ...NO_CHANGE, ready: this.ready };
    }
    this.remoteAcceptedValue = true;
    const sendAccept = !this.localAcceptedValue;
    if (sendAccept) this.localAcceptedValue = true;
    return {
      accepted: true,
      declined: false,
      ignored: false,
      sendAccept,
      ready: this.ready,
    };
  }

  beginRestart(): boolean {
    if (!this.ready || this.restartingValue) return false;
    this.restartingValue = true;
    return true;
  }

  snapshot(): RematchAgreementSnapshot {
    return {
      round: this.round,
      proposedMatchId: this.proposedMatchId,
      localRequested: this.localRequestedValue,
      remoteRequested: this.remoteRequestedValue,
      localAccepted: this.localAcceptedValue,
      remoteAccepted: this.remoteAcceptedValue,
      declined: this.declinedValue,
      restarting: this.restartingValue,
      ready: this.ready,
    };
  }
}
